/**
 * 组织切换组件
 * 用于在租户内切换当前用户的工作组织
 *
 * 功能：
 * - 显示当前组织名称
 * - 下拉展示用户可访问的所有组织
 * - 点击切换到目标组织（会刷新Token）
 * - 支持移动端响应式布局
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown, Space, Typography, Spin, Tag } from 'antd';
import { message } from '@/utils/antdStatic';
import type { MenuProps } from 'antd';
import {
  ApartmentOutlined,
  CheckOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getMyOrganizations,
  switchOrganization,
  getRelationTypeLabel,
  type MyOrganization,
} from '@/api/organization';
import { STORAGE_KEYS, setStorageItem } from '@/utils/storage';
import './index.less';

const { Text } = Typography;

interface OrganizationSwitcherProps {
  /** 是否为紧凑模式（移动端） */
  compact?: boolean;
  /** 切换成功后的回调 */
  onSwitchSuccess?: (orgId: number, orgName: string) => void;
  /** 自定义类名 */
  className?: string;
}

/**
 * 组织切换组件
 */
const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({
  compact = false,
  onSwitchSuccess,
  className,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [organizations, setOrganizations] = useState<MyOrganization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<MyOrganization | null>(null);

  // 加载用户可访问的组织列表
  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getMyOrganizations();
      if (result && result.items) {
        setOrganizations(result.items);
        // 设置当前组织（响应数据经过 keysToCamel 转换）
        const current = result.items.find((org: any) => org.isCurrent ?? org.is_current);
        if (current) {
          setCurrentOrg(current);
        } else if (result.items.length > 0) {
          // 如果没有标记当前组织，使用主组织或第一个
          const primary = result.items.find((org: any) => org.isPrimary ?? org.is_primary);
          setCurrentOrg(primary || result.items[0]);
        }
      }
    } catch (error) {
      console.error('加载组织列表失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 组件挂载时加载组织列表
  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  // 处理组织切换
  const handleSwitchOrg = useCallback(async (orgId: number) => {
    if (currentOrg && orgId === currentOrg.id) {
      return; // 已经在当前组织
    }

    setSwitching(true);
    try {
      const result = await switchOrganization(orgId);
      if (result && result.token) {
        // 更新Token
        setStorageItem(STORAGE_KEYS.TOKEN, result.token);

        // 更新当前组织状态
        const newCurrentOrg = organizations.find((org) => org.id === orgId);
        if (newCurrentOrg) {
          setCurrentOrg(newCurrentOrg);
        }

        const currentOrgData = (result as any).currentOrg ?? (result as any).current_org;
        message.success(t('organization.switchSuccess', { name: currentOrgData?.name }));

        // 触发回调
        onSwitchSuccess?.(orgId, currentOrgData?.name);

        // 刷新页面以重新加载数据
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        message.error(t('organization.switchFailed'));
      }
    } catch (error) {
      console.error('切换组织失败:', error);
      message.error(t('organization.switchFailed'));
    } finally {
      setSwitching(false);
    }
  }, [currentOrg, organizations, onSwitchSuccess, t]);

  // 构建下拉菜单项
  const menuItems: MenuProps['items'] = organizations.map((org) => ({
    key: org.id.toString(),
    label: (
      <div className="org-menu-item">
        <Space>
          <ApartmentOutlined
            style={{
              color: currentOrg?.id === org.id ? '#1890ff' : '#8c8c8c',
              fontSize: 16,
            }}
          />
          <div className="org-info">
            <Text
              strong={currentOrg?.id === org.id}
              style={{ color: currentOrg?.id === org.id ? '#1890ff' : undefined }}
            >
              {(org as any).shortName || org.short_name || org.name}
            </Text>
            {((org as any).isPrimary ?? org.is_primary) && (
              <Tag color="green" style={{ marginLeft: 4, fontSize: 10 }}>
                {t('organization.primary')}
              </Tag>
            )}
            {((org as any).relationTypes ?? org.relation_types)?.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 4, fontSize: 10 }}>
                {getRelationTypeLabel(((org as any).relationTypes ?? org.relation_types)[0])}
              </Tag>
            )}
          </div>
        </Space>
        {currentOrg?.id === org.id && (
          <CheckOutlined style={{ color: '#1890ff' }} />
        )}
      </div>
    ),
    onClick: () => handleSwitchOrg(org.id),
    disabled: switching,
  }));

  // 如果只有一个组织或没有组织，不显示切换器
  if (organizations.length <= 1 && !loading) {
    return null;
  }

  // 紧凑模式（移动端）
  if (compact) {
    return (
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomRight"
        disabled={switching}
      >
        <div className={`org-switcher-compact ${className || ''}`}>
          {switching ? (
            <Spin size="small" />
          ) : (
            <ApartmentOutlined style={{ fontSize: 18 }} />
          )}
        </div>
      </Dropdown>
    );
  }

  // 正常模式
  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
      placement="bottomRight"
      disabled={switching}
    >
      <div className={`org-switcher ${className || ''}`}>
        <Space>
          <ApartmentOutlined style={{ fontSize: 16, color: '#1890ff' }} />
          <Text className="org-name" ellipsis style={{ maxWidth: 100 }}>
            {(currentOrg as any)?.shortName || currentOrg?.short_name || currentOrg?.name || t('organization.selectOrg')}
          </Text>
          {switching ? <Spin size="small" /> : <DownOutlined style={{ fontSize: 10 }} />}
        </Space>
      </div>
    </Dropdown>
  );
};

export default OrganizationSwitcher;
